import React from 'react';
import {
  AbsoluteFill, Img, Loop, OffthreadVideo, Sequence, staticFile, spring, interpolate, Easing,
  useCurrentFrame, useVideoConfig, delayRender, continueRender,
} from 'remotion';
import {loadFont as loadDeva} from '@remotion/google-fonts/NotoSansDevanagari';
import {loadFont as loadInter} from '@remotion/google-fonts/Inter';
import {GROUNDS, PLATES, TEXTS, LABELS, VS, WORDS, GROUND_LOOP, Plate, Text, Label} from './timeline';
import {ALPHA_META} from './alpha_meta';

/**
 * गोत्र EP01 v20 — "lit ground + flat evidence + H3 film".
 * LAWS (sanatan-decode-motion §1 + §8b + §8d): animate ON TWOS · nothing fades, everything swings (damping 8, pivot
 * above) or is simply not mounted · one instance per object · ONE accent (amber on the dark ground) · the ground is
 * STATIC while resident and travels only across the act cut · one bold move per beat · text is anchored to the WTS word
 * and lives in flow layout · every visual element carries data-audit.
 * Timeline is GENERATED (gotra_build/remotion/build_timeline.py) — no hand-typed seconds in this file.
 */

const {fontFamily: devaBase} = loadDeva('normal', {weights: ['400', '600', '700', '800'], subsets: ['devanagari', 'latin']});
const {fontFamily: interBase} = loadInter('normal', {weights: ['500', '700'], subsets: ['latin']});
const deva = `${devaBase}, ${interBase}, sans-serif`;

const CREAM = '#F2E9D8';
const AMBER = '#F0913A';
const SCRIM = 'rgba(12, 9, 6, 0.62)';

const useStep = () => Math.floor(useCurrentFrame() / 2) * 2;
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
const wf = (i: number, fps: number) => Math.round(WORDS[i][0] * fps);
const swingIn = (f: number, at: number, fps: number) =>
  spring({frame: Math.max(0, f - at), fps, config: {damping: 8, stiffness: 95, mass: 1.15}});
const popIn = (f: number, at: number, fps: number) =>
  spring({frame: Math.max(0, f - at), fps, config: {damping: 9, stiffness: 150, mass: 0.8}});
const exitP = (f: number, out: number, len = 10) =>
  interpolate(clamp01((f - (out - len)) / len), [0, 1], [0, 1], {easing: Easing.in(Easing.cubic)});

/* ── GROUND: pre-baked blurred+dimmed ping-pong loop; static while resident; a 0.5 s push only across the act cut ── */
const Grounds: React.FC<{f: number}> = ({f}) => (
  <>
    {GROUNDS.map((g) => {
      if (f < g.inFrame - 2 || f >= g.outFrame) return null;
      const toCut = g.outFrame - f;                      // push-in during the last 12 frames of the act
      const push = toCut <= 12 ? 1 + 0.06 * (1 - toCut / 12) : 1;
      const loop = GROUND_LOOP[g.id] ?? 384;
      return (
        <div key={g.id} data-audit={`ground:${g.id}`}
             style={{position: 'absolute', inset: 0, transform: `scale(${push})`, transformOrigin: '50% 50%'}}>
          <Sequence from={g.inFrame} durationInFrames={g.outFrame - g.inFrame} layout="none">
            <Loop durationInFrames={loop} layout="none">
              <OffthreadVideo src={staticFile(g.asset)} muted style={{position: 'absolute', inset: 0, width: '100%', height: '100%'}} />
            </Loop>
          </Sequence>
        </div>
      );
    })}
  </>
);

/* ── the swing: pivot above the object, underdamped so it rings; out = up and off before the cut ── */
const Swing: React.FC<{f: number; inAt: number; out: number; fps: number; children: React.ReactNode; idle?: boolean}> =
  ({f, inAt, out, fps, children, idle = true}) => {
    const s = swingIn(f, inAt, fps);
    const e = exitP(f, out);
    const pend = idle ? 0.35 * Math.sin(((f - inAt) / (fps * 3.1)) * Math.PI * 2) : 0;
    const ty = (1 - s) * -160 - e * 1400;
    const rot = (1 - s) * -9 + pend + e * 7;
    return (
      <div style={{position: 'absolute', inset: 0, transform: `translateY(${ty}px) rotate(${rot}deg)`, transformOrigin: '50% -28%'}}>
        {children}
      </div>
    );
  };

/* ── ALPHA PLATE: one 4K RGBA layer, full-canvas, fitted so its art stays above the caption band ── */
const AlphaPlate: React.FC<{p: Plate; f: number; fps: number}> = ({p, f, fps}) => {
  const m = ALPHA_META[p.id] ?? {top: 0.05, bottom: 0.9, left: 0.1, right: 0.9};
  const hasLabels = LABELS.some((l) => l.plate === p.id);
  const fit = Math.min(1, (hasLabels ? 0.76 : 0.84) / m.bottom);
  return (
    <Swing f={f} inAt={p.inFrame} out={p.outFrame} fps={fps}>
      <div style={{position: 'absolute', inset: 0, transform: `scale(${fit})`, transformOrigin: `50% ${m.top * 100}%`}}>
        <Img src={staticFile(p.asset)} style={{position: 'absolute', inset: 0, width: '100%', height: '100%'}} />
        {/* the audit box is the ART, not the canvas */}
        <div data-audit={`plate:${p.id}`} style={{position: 'absolute', left: `${m.left * 100}%`, top: `${m.top * 100}%`,
             width: `${(m.right - m.left) * 100}%`, height: `${(m.bottom - m.top) * 100}%`}} />
      </div>
    </Swing>
  );
};

/* ── GRID PLATE: N panels, all blurred + dimmed; each sharpens on its word; spoken ones settle to 80 % ── */
const REST = {blur: 14, br: 0.55, sc: 1.0};
const ACTIVE = {blur: 0, br: 1.0, sc: 1.04};
const SPOKEN = {blur: 0, br: 0.8, sc: 1.0};
const lerpS = (a: typeof REST, b: typeof REST, s: number) => ({
  blur: Math.max(0, a.blur + (b.blur - a.blur) * s), br: a.br + (b.br - a.br) * s, sc: a.sc + (b.sc - a.sc) * s,
});
const GridPlate: React.FC<{p: Plate; f: number; fps: number}> = ({p, f, fps}) => {
  const {rows, cols, panels} = p.grid!;
  const trig = panels.map((wl) => wl.map((w) => wf(w, fps)).sort((a, b) => a - b));
  const all = trig.flatMap((t, i) => t.map((x) => [x, i] as [number, number])).sort((a, b) => a[0] - b[0]);
  return (
    <Swing f={f} inAt={p.inFrame} out={p.outFrame} fps={fps} idle={false}>
      <div data-audit={`grid:${p.id}`} style={{position: 'absolute', inset: 0}}>
        {panels.map((wl, i) => {
          const r = Math.floor(i / cols), c = i % cols;
          const mine = trig[i].filter((t) => t <= f);
          const a = mine.length ? mine[mine.length - 1] : -1;                 // my latest trigger
          const later = all.filter(([t, j]) => j !== i && t > a && t <= f);     // someone else spoke after me
          const u = later.length ? later[0][0] : -1;
          let st = REST;
          if (a >= 0) {
            const before = mine.length > 1 ? SPOKEN : REST;                   // re-mentioned panels rise from SPOKEN
            const sa = swingIn(f, a, fps);
            st = lerpS(before, ACTIVE, sa);
            if (u >= 0) st = lerpS(ACTIVE, SPOKEN, popIn(f, u, fps));
          }
          const isActive = a >= 0 && u < 0;
          const label = wl.map((w) => WORDS[w][2].replace(/[,।]/g, '')).join(' ');
          const ls = a >= 0 ? popIn(f, a, fps) : 0;
          return (
            <div key={i} data-audit={`gridcell:${p.id}-${i}`}
                 style={{position: 'absolute', left: `${(c / cols) * 100}%`, top: `${(r / rows) * 100}%`,
                         width: `${100 / cols}%`, height: `${100 / rows}%`, overflow: 'hidden',
                         transform: `scale(${st.sc})`, transformOrigin: '50% 50%', zIndex: isActive ? 3 : 1}}>
              <Img src={staticFile(p.asset)}
                   style={{position: 'absolute', left: `${-c * 100}%`, top: `${-r * 100}%`, width: `${cols * 100}%`, height: `${rows * 100}%`, maxWidth: 'none', maxHeight: 'none',
                           filter: `blur(${st.blur.toFixed(1)}px) brightness(${st.br.toFixed(3)})`}} />
              {a >= 0 ? (
                <div style={{position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', paddingBottom: '5%'}}>
                  <div data-audit={`label:${p.id}-${i}`}
                       style={{fontFamily: deva, fontSize: cols >= 5 ? 40 : 48, fontWeight: 700, color: isActive ? AMBER : CREAM, lineHeight: 1.3,
                               padding: '6px 22px', borderRadius: 12, background: SCRIM, textAlign: 'center',
                               transform: `translateY(${(1 - ls) * 40}px) scale(${0.7 + 0.3 * ls})`}}>
                    {label}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </Swing>
  );
};

/* ── FILM: full-frame H3 clip, hard cut, last frame held when narration runs longer ── */
const Film: React.FC<{p: Plate}> = ({p}) => {
  const clip = Math.min(p.clipFrames!, p.outFrame - p.inFrame);
  return (
    <div data-audit={`film:${p.id}`} style={{position: 'absolute', inset: 0}}>
      <Sequence from={p.inFrame} durationInFrames={clip} layout="none">
        <OffthreadVideo src={staticFile(p.asset)} muted style={{position: 'absolute', inset: 0, width: '100%', height: '100%'}} />
      </Sequence>
      {p.holdFrom! < p.outFrame ? (
        <Sequence from={p.holdFrom!} durationInFrames={p.outFrame - p.holdFrom!} layout="none">
          <Img src={staticFile(p.asset.replace('_film.mp4', '_last.png'))} style={{position: 'absolute', inset: 0, width: '100%', height: '100%'}} />
        </Sequence>
      ) : null}
    </div>
  );
};

/* ── the accepted विष्णु स्मृति beat, ported to 24 fps absolute frames (VishnuSmritiFlow.tsx) ── */
const vsrc = (n: string) => staticFile(`vsflow/${n}.png`);
const VLayer: React.FC<{name: string; style?: React.CSSProperties}> = ({name, style}) => (
  <Img src={vsrc(name)} style={{position: 'absolute', inset: 0, width: '100%', height: '100%', ...style}} />
);
const VsFlow: React.FC<{p: Plate; f: number; fps: number}> = ({p, f, fps}) => {
  const since = (k: keyof typeof VS) => f - (VS[k] as number);
  const inn = (k: keyof typeof VS, damping = 16, mass = 0.7) =>
    spring({frame: Math.max(0, since(k)), fps, config: {damping, mass, stiffness: 190}});
  const live = (k: keyof typeof VS) => since(k) >= 0;
  const pth = inn('pothi', 18, 0.9);
  const lines = clamp01(since('lines') / (fps * 1.35));
  const relight = (k: number) => {
    const at = VS.relight[k];
    return f >= at ? interpolate(f - at, [0, 6, 22], [0, 1, 0], {extrapolateRight: 'clamp'}) : 0;
  };
  const panel = (k: 'panelL' | 'panelM' | 'panelR', ox: string) => {
    if (!live(k)) return null;
    const s = inn(k, 15, 0.8);
    return <VLayer name={k} style={{transform: `translateY(${(1 - s) * 300}px) scale(${0.965 + 0.035 * s})`, transformOrigin: `${ox} 82%`}} />;
  };
  const seal = (k: 'seal1' | 'seal2' | 'seal3', ox: string, idx: number) => {
    if (!live(k)) return null;
    const s = inn(k, 11, 0.55);
    const pulse = 1 + 0.14 * relight(idx);
    return <VLayer name={k} style={{transform: `scale(${(2.35 - 1.35 * s) * pulse}) rotate(${-14 * (1 - s)}deg)`, transformOrigin: `${ox} 52%`}} />;
  };
  return (
    <Swing f={f} inAt={p.inFrame} out={p.outFrame} fps={fps} idle={false}>
      <div data-audit="plate:VS" style={{position: 'absolute', inset: 0, backgroundColor: '#F4DFBC'}}>
        <VLayer name="vs_bg_plate" />
        <VLayer name="static" />
        <VLayer name="pothi" style={{transform: `translateY(${(1 - pth) * -560}px) scale(${1.05 - 0.05 * pth})`, transformOrigin: '50% 20%'}} />
        {lines > 0 ? <VLayer name="lines" style={{clipPath: `inset(0 ${50 - 50 * lines}% 0 ${50 - 50 * lines}%)`}} /> : null}
        {panel('panelL', '16.7%')}
        {seal('seal1', '17.1%', 0)}
        {panel('panelM', '50%')}
        {seal('seal2', '50%', 1)}
        {panel('panelR', '83.5%')}
        {seal('seal3', '82.6%', 2)}
      </div>
    </Swing>
  );
};

/* ── words that land on their spoken time; newest word is the one accent ── */
const WordRow: React.FC<{words: number[]; f: number; fps: number; size: number; weight?: number; maxWidth?: string}> =
  ({words, f, fps, size, weight = 700, maxWidth = '82%'}) => {
    const mounted = words.filter((w) => wf(w, fps) <= f);
    if (!mounted.length) return null;
    const last = mounted[mounted.length - 1];
    return (
      <div style={{display: 'inline-flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'baseline', columnGap: size * 0.38, rowGap: 6,
                   maxWidth, padding: `${size * 0.22}px ${size * 0.75}px`, borderRadius: 16, background: SCRIM, boxShadow: '0 10px 36px rgba(0,0,0,0.35)'}}>
        {mounted.map((w) => {
          const s = popIn(f, wf(w, fps), fps);
          return (
            <span key={w} style={{fontFamily: deva, fontSize: size, fontWeight: weight, lineHeight: 1.36, color: w === last ? AMBER : CREAM,
                                 display: 'inline-block', transform: `translateY(${(1 - s) * size * 0.55}px) scale(${0.72 + 0.28 * s})`}}>
              {WORDS[w][2]}
            </span>
          );
        })}
      </div>
    );
  };

const TextBlock: React.FC<{t: Text; f: number; fps: number}> = ({t, f, fps}) => {
  const e = exitP(f, t.outFrame, 8);
  const cfg = t.kind === 'heading' ? {top: '38%', size: 86} : t.kind === 'oneliner' ? {top: '41%', size: 62} : {top: '86.5%', size: 46};
  return (
    <div data-audit={`${t.kind}:${t.id}`}
         style={{position: 'absolute', left: 0, right: 0, top: cfg.top, display: 'flex', justifyContent: 'center',
                 transform: `translateY(${e * (t.kind === 'caption' ? 260 : -900)}px)`}}>
      <WordRow words={t.words} f={f} fps={fps} size={cfg.size} weight={t.kind === 'heading' ? 800 : 700} />
    </div>
  );
};

/* ── label chips: accumulate on their words, stay for the plate; films get a lower-third row ── */
const LabelRow: React.FC<{plate: Plate; f: number; fps: number}> = ({plate, f, fps}) => {
  const mine = LABELS.filter((l) => l.plate === plate.id && f >= l.inFrame && f < l.outFrame);
  if (!mine.length) return null;
  const e = exitP(f, plate.outFrame, 8);
  const film = plate.kind === 'film';
  return (
    <div style={{position: 'absolute', left: 0, right: 0, top: film ? '73%' : '77.5%', display: 'flex', justifyContent: 'center', columnGap: 22,
                 transform: `translateY(${e * 260}px)`}}>
      {mine.map((l: Label) => {
        const s = popIn(f, l.inFrame, fps);
        const last = mine[mine.length - 1].id === l.id;
        return (
          <div key={l.id} data-audit={`label:${l.id}`}
               style={{fontFamily: deva, fontSize: film ? 58 : 44, fontWeight: 800, color: last ? AMBER : CREAM, lineHeight: 1.3,
                       padding: film ? '10px 34px' : '6px 24px', borderRadius: 14, background: SCRIM,
                       transform: `translateY(${(1 - s) * 60}px) scale(${0.7 + 0.3 * s})`}}>
            {l.text}
          </div>
        );
      })}
    </div>
  );
};

/* ── the two Remotion-only cards ── */
const Card: React.FC<{p: Plate; f: number; fps: number}> = ({p, f, fps}) => {
  const wa = p.id === 'WA';
  const line1 = wa ? [1594, 1595, 1596] : [1597, 1598, 1599, 1600, 1601, 1602];
  const line2 = wa ? [] : [1604, 1605, 1606];
  const ls = swingIn(f, p.inFrame, fps);
  return (
    <Swing f={f} inAt={p.inFrame} out={p.id === 'END' ? p.outFrame + 100 : p.outFrame} fps={fps} idle={false}>
      <div style={{position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', rowGap: 34}}>
        <Img data-audit={`logo:${p.id}`} src={staticFile('logo/logo.png')}
             style={{height: wa ? 400 : 420, borderRadius: 22, transform: `scale(${0.8 + 0.2 * ls})`, boxShadow: '0 20px 60px rgba(0,0,0,0.45)'}} />
        <div data-audit={`cardtext:${p.id}`} style={{display: 'flex', flexDirection: 'column', alignItems: 'center', rowGap: 18}}>
          <WordRow words={line1} f={f} fps={fps} size={wa ? 64 : 58} weight={800} />
          {line2.length ? <WordRow words={line2} f={f} fps={fps} size={76} weight={800} /> : null}
        </div>
      </div>
    </Swing>
  );
};

/* ── layout auditor (audit_gotra.mjs reads AUDIT:: from the console). Measures only once fonts + images are loaded,
   relative to the composition root (the renderer parks the page off-screen until it is ready). ── */
const AuditProbe: React.FC = () => {
  const frame = useCurrentFrame();
  React.useEffect(() => {
    const handle = delayRender('audit-measure');
    let tries = 0;
    const measure = () => {
      const imgs = Array.from(document.images);
      if (!imgs.every((i) => i.complete) && tries++ < 240) { requestAnimationFrame(measure); return; }
      const root = document.querySelector('[data-audit-root]') as HTMLElement | null;
      const rr = root ? root.getBoundingClientRect() : ({x: 0, y: 0} as DOMRect);
      const boxes = Array.from(document.querySelectorAll('[data-audit]')).map((e) => {
        const r = (e as HTMLElement).getBoundingClientRect();
        const st = getComputedStyle(e as HTMLElement);
        return {n: e.getAttribute('data-audit'), x: +(r.x - rr.x).toFixed(1), y: +(r.y - rr.y).toFixed(1), w: +r.width.toFixed(1), h: +r.height.toFixed(1),
                o: +(+st.opacity).toFixed(2), t: ((e as HTMLElement).innerText || '').slice(0, 22).replace(/\s+/g, ' ')};
      });
      // eslint-disable-next-line no-console
      console.log('AUDIT::' + JSON.stringify({frame, boxes}));
      continueRender(handle);
    };
    (document as any).fonts?.ready ? (document as any).fonts.ready.then(() => requestAnimationFrame(measure)) : requestAnimationFrame(measure);
  });
  return null;
};

export const GotraV20: React.FC = () => {
  const f = useStep();
  const {fps} = useVideoConfig();
  const plates = PLATES.filter((p) => f >= p.inFrame && f < p.outFrame);
  const texts = TEXTS.filter((t) => f >= t.inFrame && f < t.outFrame);
  return (
    <AbsoluteFill data-audit-root style={{backgroundColor: '#120C08', fontFamily: deva}}>
      <AbsoluteFill style={{zIndex: 0}}>
        <Grounds f={f} />
      </AbsoluteFill>
      <AbsoluteFill style={{zIndex: 10}}>
        {plates.map((p) => {
          if (p.kind === 'alpha') return <AlphaPlate key={p.id} p={p} f={f} fps={fps} />;
          if (p.kind === 'grid') return <GridPlate key={p.id} p={p} f={f} fps={fps} />;
          if (p.kind === 'film') return <Film key={p.id} p={p} />;
          if (p.kind === 'vsflow') return <VsFlow key={p.id} p={p} f={f} fps={fps} />;
          if (p.kind === 'card') return <Card key={p.id} p={p} f={f} fps={fps} />;
          return null;
        })}
      </AbsoluteFill>
      <AbsoluteFill style={{zIndex: 20}}>
        {plates.map((p) => <LabelRow key={`l-${p.id}`} plate={p} f={f} fps={fps} />)}
        {texts.map((t) => <TextBlock key={t.id} t={t} f={f} fps={fps} />)}
      </AbsoluteFill>
      <AuditProbe />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
